import { defineComponent as _defineComponent } from 'vue';
import { faCaretDown, faCaretUp, faExclamation, faLink, faPlus, faSpinner, faUnlink, } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { BAlert, BButton, BButtonGroup, BCollapse, BFormCheckbox, BTooltip } from "bootstrap-vue";
import { storeToRefs } from "pinia";
import { computed, onMounted, ref, watch } from "vue";
import { isDatasetElement, isDCE } from "@/api";
import { getGalaxyInstance } from "@/app";
import { useDatatypesMapper } from "@/composables/datatypesMapper";
import { useUid } from "@/composables/utils/uid";
import { useEventStore } from "@/stores/eventStore";
import { useHistoryStore } from "@/stores/historyStore";
import { orList } from "@/utils/strings";
import { BATCH, SOURCE, VARIANTS } from "./variants";
import FormSelection from "../FormSelection.vue";
import CollectionCreatorModal from "@/components/Collections/CollectionCreatorModal.vue";
import FormSelect from "@/components/Form/Elements/FormSelect.vue";
import HelpText from "@/components/Help/HelpText.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'FormData',
    props: {
        loading: { type: Boolean, default: false },
        multiple: { type: Boolean, default: false },
        optional: { type: Boolean, default: false },
        options: null,
        value: { default: undefined },
        extensions: { default: () => [] },
        type: { default: "data" },
        collectionTypes: { default: undefined },
        flavor: { default: undefined },
        tag: { default: undefined }
    },
    emits: ["input", "alert"],
    setup(__props, { emit: $emit }) {
        const props = __props;
        const COLLECTION_TYPE_TO_LABEL = {
            list: "list",
            "list:paired": "list of dataset pairs",
            paired: "dataset pair",
        };
        const eventStore = useEventStore();
        const { datatypesMapper } = useDatatypesMapper();
        // Determines wether values should be processed as linked or unlinked
        const currentLinked = ref(true);
        // Indicates which of the select field from the set of variants is currently shown
        const currentField = ref(0);
        // Field highlighting status
        const currentHighlighting = ref(null);
        // Drag/Drop related values
        const dragData = ref(null);
        const dragTarget = ref(null);
        // Collection creator modal settings
        const collectionModalShow = ref(false);
        const collectionModalType = ref("list");
        const { currentHistoryId } = storeToRefs(useHistoryStore());
        const restrictsExtensions = computed(() => {
            const extensions = props.extensions;
            if (!extensions || extensions.length == 0 || extensions.indexOf("data") >= 0) {
                return false;
            }
            else {
                return true;
            }
        });
        /** Store options which need to be preserved **/
        const keepOptions = {};
        /**
         * Determine whether the file dialog can be used or not
         */
        const canBrowse = computed(() => variant.value && !!variant.value.find((v) => v.src === SOURCE.DATASET));
        /**
         * Provides the currently shown source type
         */
        const currentSource = computed(() => (currentVariant.value ? currentVariant.value.src : null));
        /**
         * Interface between incoming input value and select field value
         */
        const currentValue = computed({
            get: () => {
                const value = [];
                if (props.value) {
                    for (const v of props.value.values) {
                        const foundEntry = formattedOptions.value.find((entry) => entry.value && entry.value.id === v.id && entry.value.src === v.src);
                        if (foundEntry && foundEntry.value) {
                            value.push(foundEntry.value);
                            if (!currentVariant.value?.multiple) {
                                break;
                            }
                        }
                    }
                    if (value.length > 0) {
                        return value;
                    }
                }
                if (!currentVariant.value?.multiple && !props.optional && formattedOptions.value.length > 0) {
                    const firstEntry = formattedOptions.value && formattedOptions.value[0];
                    if (firstEntry && firstEntry.value) {
                        value.push(firstEntry.value);
                        return value;
                    }
                }
                return undefined;
            },
            set: (val) => {
                if (val && Array.isArray(val) && val.length > 0) {
                    val.sort((a, b) => {
                        const aHid = a.hid;
                        const bHid = b.hid;
                        if (aHid && bHid) {
                            return aHid - bHid;
                        }
                        else {
                            return 0;
                        }
                    });
                }
                $emit("input", createValue(val));
            },
        });
        /**
         * Returns the variant i.e. attributes, of the shown select field.
         */
        const currentVariant = computed(() => {
            if (variant.value && currentField.value < variant.value.length) {
                return variant.value[currentField.value];
            }
            else {
                return null;
            }
        });
        /**
         * Converts and populates options for the shown select field
         */
        const formattedOptions = computed(() => {
            if (currentSource.value && currentSource.value in props.options) {
                // Map incoming values to available options
                const options = props.options[currentSource.value] || [];
                const result = [];
                options.forEach((option) => {
                    const newOption = {
                        label: `${option.hid}: ${option.name}`,
                        value: option || null,
                    };
                    if (option.keep) {
                        const keepKey = `${option.id}_${option.src}`;
                        keepOptions[keepKey] = newOption;
                    }
                    else {
                        const accepted = !props.tag || option.tags?.includes(props.tag);
                        if (accepted) {
                            result.push(newOption);
                        }
                    }
                });
                // Add keep-options from other sources
                const otherSources = [SOURCE.COLLECTION_ELEMENT, SOURCE.LIBRARY_DATASET];
                for (const otherSource of otherSources) {
                    const otherOptions = props.options[otherSource];
                    if (Array.isArray(otherOptions)) {
                        otherOptions.forEach((option) => {
                            const keepKey = `${option.id}_${option.src}`;
                            const sourceLabel = getSourceLabel(getSourceType(option));
                            const newOption = {
                                label: `${option.name} (as ${sourceLabel})`,
                                value: option || null,
                            };
                            keepOptions[keepKey] = newOption;
                        });
                    }
                }
                // Populate keep-options from cache
                Object.entries(keepOptions).forEach(([key, option]) => {
                    if (option.value && getSourceType(option.value) === currentSource.value) {
                        result.unshift(option);
                    }
                });
                // Sort entries by hid
                result.sort((a, b) => {
                    const aHid = a.value && a.value.hid;
                    const bHid = b.value && b.value.hid;
                    if (aHid && bHid) {
                        return bHid - aHid;
                    }
                    else {
                        return 0;
                    }
                });
                // Add optional entry
                if (!currentVariant.value?.multiple && props.optional) {
                    result.unshift({
                        label: "Nothing selected",
                        value: null,
                    });
                }
                return result;
            }
            else {
                return [];
            }
        });
        /**
         * Provides placeholder label for select field
         */
        const placeholder = computed(() => getSourceLabel(currentSource.value));
        /**
         * Provides the array of available variants associated with a specific form data type
         */
        const variant = computed(() => {
            const flavorKey = props.flavor ? `${props.flavor}_` : "";
            const multipleKey = props.multiple ? `_multiple` : "";
            const variantKey = `${flavorKey}${props.type}${multipleKey}`;
            return VARIANTS[variantKey];
        });
        /**
         * Clears highlighting with delay
         */
        function clearHighlighting(timeout = 1000) {
            setTimeout(() => {
                currentHighlighting.value = null;
            }, timeout);
        }
        /**
         * Create final input element value
         */
        function createValue(val) {
            if (val) {
                const values = Array.isArray(val) ? val : [val];
                if (variant.value && values.length > 0 && values[0]) {
                    const hasMapOverType = values.find((v) => !!v.map_over_type);
                    const isMultiple = values.length > 1;
                    // Determine source representation (uses only the initial value)
                    const sourceType = getSourceType(values[0]);
                    // Identify matching variant
                    const variantIndex = variant.value.findIndex((v) => (!isMultiple || v.multiple === isMultiple) && v.src === sourceType);
                    // Determine batch mode
                    let batch = BATCH.DISABLED;
                    if (variantIndex >= 0) {
                        const variantDetails = variant.value[variantIndex];
                        if (variantDetails) {
                            // Switch to another field type if source differs from current field
                            if (isMultiple || (currentVariant.value && currentVariant.value.src !== sourceType)) {
                                currentField.value = variantIndex;
                                batch = variantDetails.batch;
                            }
                            else {
                                batch = (currentVariant.value && currentVariant.value.batch) || BATCH.DISABLED;
                            }
                        }
                    }
                    // Emit new value
                    return {
                        batch: batch !== BATCH.DISABLED || !!hasMapOverType,
                        product: batch === BATCH.ENABLED && !currentLinked.value,
                        values: values.map((entry) => ({
                            id: entry.id,
                            src: entry.src,
                            map_over_type: entry.map_over_type || null,
                        })),
                    };
                }
            }
            else {
                return null;
            }
        }
        /**
         * Return user-friendly data source label
         */
        function getSourceLabel(src) {
            return src === SOURCE.DATASET ? "dataset" : "dataset collection";
        }
        /**
         * Determine source type of a given value
         */
        function getSourceType(val) {
            if (val.src === SOURCE.COLLECTION_ELEMENT) {
                return val.is_dataset ? SOURCE.DATASET : SOURCE.COLLECTION;
            }
            else if (val.src === SOURCE.LIBRARY_DATASET) {
                return SOURCE.DATASET;
            }
            else {
                return val.src;
            }
        }
        /** Add values from drag/drop or data dialog sources */
        function handleIncoming(incoming, partial = true) {
            if (incoming) {
                const values = Array.isArray(incoming) ? incoming : [incoming];
                const extensions = values.map((v) => v.extension || v.elements_datatypes).filter((v) => (v ? true : false));
                if (!canAcceptDatatype(extensions)) {
                    return false;
                }
                if (values.some((v) => !canAcceptSrc(v.history_content_type, v.collection_type))) {
                    return false;
                }
                if (values.length > 0) {
                    const incomingValues = [];
                    values.forEach((v) => {
                        // Map incoming objects to data option values
                        let newSrc;
                        if (isDCE(v)) {
                            if (isDatasetElement(v)) {
                                newSrc = SOURCE.DATASET;
                                v = v.object;
                            }
                            else {
                                newSrc = SOURCE.COLLECTION_ELEMENT;
                            }
                        }
                        else {
                            newSrc =
                                v.src || (v.history_content_type === "dataset_collection" ? SOURCE.COLLECTION : SOURCE.DATASET);
                        }
                        const newHid = v.hid;
                        const newId = v.id;
                        const newName = v.name ? v.name : newId;
                        const newValue = {
                            id: newId,
                            src: newSrc,
                            batch: false,
                            map_over_type: undefined,
                            hid: newHid,
                            name: newName,
                            keep: true,
                            tags: [],
                        };
                        if (v.collection_type && props.collectionTypes?.length > 0) {
                            if (!props.collectionTypes.includes(v.collection_type)) {
                                const mapOverType = props.collectionTypes.find((collectionType) => v.collection_type.endsWith(collectionType));
                                if (!mapOverType) {
                                    return false;
                                }
                                newValue["batch"] = true;
                                newValue["map_over_type"] = mapOverType;
                            }
                        }
                        // Verify that new value has corresponding option
                        const keepKey = `${newId}_${newSrc}`;
                        const existingOptions = props.options && props.options[newSrc];
                        const foundOption = existingOptions && existingOptions.find((option) => option.id === newId);
                        if (!foundOption && !(keepKey in keepOptions)) {
                            keepOptions[keepKey] = { label: `${newHid || "Selected"}: ${newName}`, value: newValue };
                        }
                        // Add new value to list
                        incomingValues.push(newValue);
                    });
                    if (incomingValues.length > 0 && incomingValues[0]) {
                        // Set new value
                        const config = currentVariant.value;
                        const firstValue = incomingValues[0];
                        if (config && config.src == firstValue.src && partial) {
                            if (config.multiple) {
                                const newValues = currentValue.value ? currentValue.value.slice() : [];
                                incomingValues.forEach((v) => {
                                    newValues.push(v);
                                });
                                currentValue.value = newValues;
                            }
                            else {
                                currentValue.value = [firstValue];
                            }
                        }
                        else {
                            currentValue.value = incomingValues;
                        }
                    }
                }
            }
            return true;
        }
        /**
         * Open file dialog
         */
        function onBrowse() {
            if (currentVariant.value) {
                const library = !!currentVariant.value.library;
                const multiple = !!currentVariant.value.multiple;
                getGalaxyInstance().data.dialog((response) => {
                    handleIncoming(response, false);
                }, {
                    allowUpload: true,
                    format: null,
                    library,
                    multiple,
                });
            }
        }
        function canAcceptDatatype(itemDatatypes) {
            if (!(props.extensions?.length > 0) || props.extensions.includes("data")) {
                return true;
            }
            let datatypes;
            if (!Array.isArray(itemDatatypes)) {
                datatypes = [itemDatatypes];
            }
            else {
                datatypes = itemDatatypes;
            }
            const incompatibleItem = datatypes.find((extension) => !datatypesMapper.value?.isSubTypeOfAny(extension, props.extensions));
            if (incompatibleItem) {
                return false;
            }
            return true;
        }
        function canAcceptSrc(historyContentType, collectionType) {
            if (historyContentType === "dataset") {
                // HDA can only be fed into data parameters, not collection parameters
                if (props.type === "data") {
                    return true;
                }
                else {
                    $emit("alert", "dataset is not a valid input for dataset collection parameter.");
                    return false;
                }
            }
            else if (historyContentType === "dataset_collection") {
                if (props.type === "data") {
                    // collection can always be mapped over a data input ... in theory.
                    // One day we should also validate the map over model
                    return true;
                }
                if (!collectionType) {
                    // Should always be set if item is dataset collection
                    throw Error("Item is a dataset collection of unknown type.");
                }
                else if (!props.collectionTypes) {
                    // if no collection_type is set all collections are valid
                    return true;
                }
                else {
                    if (props.collectionTypes.includes(collectionType)) {
                        return true;
                    }
                    if (props.collectionTypes.some((element) => collectionType.endsWith(element))) {
                        return true;
                    }
                    else {
                        $emit("alert", `${collectionType} dataset collection is not a valid input for ${orList(props.collectionTypes)} type dataset collection parameter.`);
                        return false;
                    }
                }
            }
            else {
                throw Error("Unknown history content type.");
            }
        }
        /** Allowed collection types for collection creation */
        const effectiveCollectionTypes = props.collectionTypes?.filter((collectionType) => ["list", "list:paired", "paired"].includes(collectionType));
        function buildNewCollection(collectionType) {
            if (!["list", "list:paired", "paired"].includes(collectionType)) {
                throw Error(`Unknown collection type: ${collectionType}`);
            }
            collectionModalType.value = collectionType;
            collectionModalShow.value = true;
        }
        function createdCollection(collection) {
            handleIncoming(collection);
        }
        // Drag/Drop event handlers
        function onDragEnter(evt) {
            const eventData = eventStore.getDragData();
            if (eventData) {
                const extensions = eventData.extension || eventData.elements_datatypes;
                let highlightingState = "success";
                if (!canAcceptDatatype(extensions)) {
                    highlightingState = "warning";
                    $emit("alert", `${extensions} is not an acceptable format for this parameter.`);
                }
                else if (!canAcceptSrc(eventData.history_content_type, eventData.collection_type)) {
                    highlightingState = "warning";
                    $emit("alert", `${eventData.history_content_type} is not an acceptable input type for this parameter.`);
                }
                currentHighlighting.value = highlightingState;
                dragTarget.value = evt.target;
                dragData.value = eventData;
            }
        }
        function onDragLeave(evt) {
            if (dragTarget.value === evt.target) {
                currentHighlighting.value = null;
                $emit("alert", undefined);
            }
        }
        function onDrop() {
            if (dragData.value) {
                let accept = false;
                if (eventStore.multipleDragData) {
                    accept = handleIncoming(Object.values(dragData.value), false);
                }
                else {
                    accept = handleIncoming(dragData.value);
                }
                if (accept) {
                    currentHighlighting.value = "success";
                }
                else {
                    currentHighlighting.value = "warning";
                }
                $emit("alert", undefined);
                dragData.value = null;
                clearHighlighting();
            }
        }
        /**
         * Matches an array of values to available options
         */
        const matchedValues = computed(() => {
            const values = [];
            if (props.value && props.value.values.length > 0) {
                props.value.values.forEach((entry) => {
                    if ("src" in entry && entry.src) {
                        const options = props.options[entry.src] || [];
                        const option = options.find((v) => v.id === entry.id && v.src === entry.src);
                        if (option) {
                            const accepted = !props.tag || option.tags?.includes(props.tag);
                            if (accepted) {
                                values.push({ ...option, name: option.name || entry.id });
                            }
                        }
                    }
                });
            }
            return values;
        });
        onMounted(() => {
            if (props.value && matchedValues.value.length > 0) {
                $emit("input", createValue(matchedValues.value));
            }
            else {
                $emit("input", createValue(currentValue.value));
            }
        });
        /**
         * Watch and set current value if user switches to a different select field
         */
        watch(() => [props.options, currentLinked.value, currentVariant.value], () => {
            $emit("input", createValue(currentValue.value));
        });
        const formatsVisible = ref(false);
        const formatsButtonId = useUid("form-data-formats-");
        function collectionTypeToText(collectionType) {
            if (collectionType == "list:paired") {
                return "list of pairs";
            }
            else {
                return collectionType;
            }
        }
        const warningListAmount = 4;
        const noOptionsWarningMessage = computed(() => {
            const itemType = props.type === "data" ? "datasets" : "dataset collections";
            const collectionTypeLabel = props.collectionTypes?.length
                ? `${orList(props.collectionTypes.map(collectionTypeToText))} `
                : "";
            if (!props.extensions || props.extensions.length === 0 || props.extensions.includes("data")) {
                return `No ${collectionTypeLabel}${itemType} available`;
            }
            else if (props.extensions.length <= warningListAmount) {
                return `No ${collectionTypeLabel}${itemType} with ${orList(props.extensions)} elements available`;
            }
            else {
                return `No compatible ${collectionTypeLabel}${itemType} available`;
            }
        });
        return { __sfc: true, COLLECTION_TYPE_TO_LABEL, props, eventStore, datatypesMapper, $emit, currentLinked, currentField, currentHighlighting, dragData, dragTarget, collectionModalShow, collectionModalType, currentHistoryId, restrictsExtensions, keepOptions, canBrowse, currentSource, currentValue, currentVariant, formattedOptions, placeholder, variant, clearHighlighting, createValue, getSourceLabel, getSourceType, handleIncoming, onBrowse, canAcceptDatatype, canAcceptSrc, effectiveCollectionTypes, buildNewCollection, createdCollection, onDragEnter, onDragLeave, onDrop, matchedValues, formatsVisible, formatsButtonId, collectionTypeToText, warningListAmount, noOptionsWarningMessage, faCaretDown, faCaretUp, faExclamation, faLink, faPlus, faSpinner, faUnlink, FontAwesomeIcon, BAlert, BButton, BButtonGroup, BCollapse, BFormCheckbox, BTooltip, BATCH, SOURCE, FormSelection, CollectionCreatorModal, FormSelect, HelpText };
    }
});
